import { useCallback, useEffect, useState } from 'react';
import { CommandPalette, Sidebar, StatusBar, TopBar } from './components/chrome.jsx';
import { Login } from './pages/Login.jsx';
import { HomePage } from './pages/Home.jsx';
import { QueryConsole } from './pages/QueryConsole.jsx';
import { MonitorPage } from './pages/Monitor.jsx';
import { ClusterPage } from './pages/Cluster.jsx';
import { UsersPage } from './pages/Users.jsx';
import { AccessPage } from './pages/Access.jsx';
import { AuditPage } from './pages/Audit.jsx';
import { FunctionsPage } from './pages/Functions.jsx';
import { ReportsPage } from './pages/Reports.jsx';
import { StoragePage } from './pages/Storage.jsx';
import { SettingsPage } from './pages/Settings.jsx';
import * as live from './data/live-store.js';
import * as auth from './data/auth-store.js';
import { getSocket, resetSocket } from './data/socket.js';
import { useClusterSummary } from './data/use-metrics.js';

// B2316: the `audit` route shows the server's audit trail (SHOW AUDIT).
const ROUTES = new Set(['home', 'query', 'monitor', 'cluster', 'users', 'access', 'audit', 'functions', 'reports', 'storage', 'settings']);

// Hash routing — `#/query`, `#/monitor`, etc. We use the URL hash (not pathname)
// so reloads work without any server-side rewrite, both behind the Vite dev
// proxy and once the bundle is embedded in the kinedb binary.
//
// A sub-path after the top-level segment is captured separately and only
// the page that owns the route interprets it (e.g. /reports/<id>/edit).
function readRouteFromHash() {
  const m = window.location.hash.match(/^#\/?([a-z]+)(?:\/(.*))?$/i);
  const top = m ? m[1].toLowerCase() : '';
  const sub = m && m[2] ? m[2].replace(/\/$/, '') : '';
  return { route: ROUTES.has(top) ? top : 'home', sub };
}

function Page({ id, sub, onNavigate }) {
  switch (id) {
    case 'home':     return <HomePage onNavigate={onNavigate} username={auth.getUsername()} />;
    case 'query':    return <QueryConsole />;
    case 'monitor':  return <MonitorPage />;
    case 'cluster':  return <ClusterPage />;
    case 'users':    return <UsersPage />;
    case 'access':   return <AccessPage sub={sub} onNavigate={onNavigate} />;
    case 'audit':    return <AuditPage />;
    case 'functions': return <FunctionsPage />;
    case 'reports':  return <ReportsPage sub={sub} onNavigate={onNavigate} />;
    case 'storage':  return <StoragePage />;
    case 'settings': return <SettingsPage />;
    default:         return <HomePage onNavigate={onNavigate} username={auth.getUsername()} />;
  }
}

// Initial theme: stored explicit choice → system preference → dark.
function readInitialTheme() {
  try {
    const stored = localStorage.getItem('kinedb.theme');
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* private mode / quota */ }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: light)').matches) {
    return 'light';
  }
  return 'dark';
}

function readSidebarCollapsed() {
  try { return localStorage.getItem('kinedb.sidebar.collapsed') === '1'; }
  catch { return false; }
}

// B05 slice 7: the login gate, and it has to sit ABOVE `AppShell` rather than
// inside it.
//
// Two things in the shell start work the instant they mount and never stop:
// `useEffect(getSocket)` dials the WebSocket (an unauthenticated socket fails its
// login, closes, and enters the 1s→30s reconnect backoff), and
// `useClusterSummary` subscribes the metrics-store singletons, which poll `/sql`
// and `/health` every 1.5-15s FOREVER once subscribed — module-level, no
// unsubscribe path. Rendering the shell first and hiding it behind an overlay
// would leave both running behind the login form. So the gate is a separate
// component whose early return means `AppShell` never mounts, and neither hook
// ever runs, until there is a session.
export default function App() {
  // 'checking' → the anonymous probe is in flight (only ever the first paint
  // with no session); 'gated' → render the login form; 'open' → the shell.
  // The probe exists because a PERMISSIVE server (the collector always;
  // scripts/collector.sh pins both planes) serves anonymously — prompting for
  // a password there asks for something the deployment does not require. An
  // enforcing server fails the probe and gates exactly as before, and a later
  // 401 clears anonymous mode via onUnauthorized, notifying this subscriber.
  const [access, setAccess] = useState(() => (auth.hasAccess() ? 'open' : 'checking'));

  // The store is also cleared from outside this component — by `socket.js` when
  // a WS login is REFUSED, by the 401 interceptor, and by the expiry check
  // inside the store itself — so the gate subscribes rather than only reacting
  // to the form.
  useEffect(() => auth.subscribe(() => setAccess(auth.hasAccess() ? 'open' : 'gated')), []);

  useEffect(() => {
    if (access !== 'checking') return undefined;
    let alive = true;
    // Success notifies subscribers (→ 'open' above); only failure needs a hand.
    auth.probeAnonymous().then((ok) => { if (alive && !ok) setAccess('gated'); });
    return () => { alive = false; };
  }, [access]);

  if (access === 'checking') return null; // one loopback round-trip, sub-100ms
  if (access !== 'open') return <Login onSuccess={() => setAccess('open')} />;
  return <AppShell />;
}

function AppShell() {
  const [{ route, sub }, setRouteState] = useState(readRouteFromHash);
  const [cmdK, setCmdK]   = useState(false);
  const [theme, setTheme] = useState(readInitialTheme);
  const [density]         = useState('comfy');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  useEffect(() => {
    try { localStorage.setItem('kinedb.sidebar.collapsed', sidebarCollapsed ? '1' : '0'); }
    catch { /* private mode */ }
  }, [sidebarCollapsed]);

  // Wrap setRoute to write the hash; the hashchange listener below mirrors
  // browser back/forward and any direct edits to location.hash back into state.
  // Optional `s` provides the sub-path (e.g. setRoute('reports', 'cluster-health')).
  const setRoute = useCallback((r, s = '') => {
    const target = s ? `/${r}/${s}` : `/${r}`;
    if (window.location.hash !== `#${target}`) {
      window.location.hash = target;        // triggers hashchange; state updates there
    } else {
      setRouteState({ route: r, sub: s });  // same hash → still ensure state is in sync
    }
  }, []);

  useEffect(() => {
    const onHash = () => setRouteState(readRouteFromHash());
    window.addEventListener('hashchange', onHash);
    // Normalize empty/unknown hashes to #/home so URL is always copyable.
    if (!window.location.hash) window.location.replace('#/' + route);
    return () => window.removeEventListener('hashchange', onHash);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-density', density);
    // Persist user's choice so reload keeps the same theme. First-load value
    // (which may have come from system pref) is saved too — toggling to follow
    // a future system change is one click on the sun/moon button.
    try { localStorage.setItem('kinedb.theme', theme); } catch { /* private mode */ }
  }, [theme, density]);

  useEffect(() => {
    const onKey = e => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdK(c => !c); }
      if (e.key === 'Escape') setCmdK(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Sidebar Query badge — sum of unseen row changes across all live tabs.
  // Subscribe once at mount; the live store debounces updates internally.
  const [queryUnseen, setQueryUnseen] = useState(() => live.totalUnseen());
  useEffect(() => live.subscribe(() => setQueryUnseen(live.totalUnseen())), []);

  // Kick off the WebSocket connection on app mount so the StatusBar dot
  // reflects real connection state without waiting for the user's first
  // SQL action. Errors are surfaced via the dot turning red, not thrown.
  useEffect(() => { getSocket().catch(() => { /* StatusBar shows offline */ }); }, []);

  // Slow-cadence cluster summary feeds the always-visible chrome (top-bar
  // status badge + sidebar footer KPIs).
  const cluster = useClusterSummary({ intervalMs: 15000 });

  // Logout revokes the persisted token server-side (`POST /logout`, in
  // auth.logout) and then clears the store, which notifies App's gate → <Login/>.
  // The socket is closed first so its reconnect loop cannot re-present the
  // token in the window before the clear. The operator-side hammer for every
  // OTHER session of a user is `ALTER USER <name> REVOKE SESSIONS`.
  const logout = useCallback(() => {
    resetSocket();          // close + cancel the armed reconnect timer
    void auth.logout();     // POST /logout (best effort) → clearSession → gate
  }, []);

  return (
    <div className="app-shell">
      <TopBar onCmdK={() => setCmdK(true)} onToggleTheme={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} onNavigate={setRoute} cluster={cluster} username={auth.getUsername()} onLogout={logout} />
      <Sidebar active={route} onNavigate={setRoute} badges={{ query: queryUnseen }} cluster={cluster} collapsed={sidebarCollapsed} onToggleCollapsed={() => setSidebarCollapsed(c => !c)} />
      <main className="main"><Page id={route} sub={sub} onNavigate={setRoute} /></main>
      <StatusBar cluster={cluster} />
      {cmdK && <CommandPalette onClose={() => setCmdK(false)} onNavigate={setRoute} />}
    </div>
  );
}
